import { DataFactory, Store } from "n3";
import type { DatasetDescriptor, OntologyVersion } from "./types";
import { resolveVersion } from "./types";
import { parseRdfFile } from "./parse";
import { PrefixTable } from "./prefixes";

const { namedNode } = DataFactory;

/**
 * Every dataset's quads are tagged into their own named graph (rather than
 * merged into the default graph) so the store can later attribute a term to
 * its declaring dataset (Files tab provenance) and so the dataset switcher
 * can filter queries by graph without needing a second parallel index.
 */
export function datasetGraphIri(datasetId: string): string {
  return `urn:ontology-explorer:dataset:${datasetId}`;
}

/** One dataset paired with the version of it that should be loaded. */
export interface DatasetSelection {
  dataset: DatasetDescriptor;
  version: OntologyVersion;
}

export interface LoadedGraph {
  store: Store;
  prefixes: PrefixTable;
  /** Named-graph IRI (as produced by `datasetGraphIri`) -> dataset id, for reverse lookups. */
  datasetIdByGraphIri: Map<string, string>;
  /** Which version of each dataset actually got loaded. */
  versionIdByDatasetId: Map<string, string>;
}

/** Resolves each dataset to its selected (or newest) version. */
export function toSelections(
  datasets: DatasetDescriptor[],
  versionSelections: ReadonlyMap<string, string> | null,
): DatasetSelection[] {
  return datasets.map((dataset) => ({
    dataset,
    version: resolveVersion(dataset, versionSelections),
  }));
}

/** Fetches (or reads inline), parses, and loads one version of every given dataset into one shared store. */
export async function loadDatasets(
  selections: DatasetSelection[],
): Promise<LoadedGraph> {
  const store = new Store();
  const prefixes = new PrefixTable();
  const datasetIdByGraphIri = new Map<string, string>();
  const versionIdByDatasetId = new Map<string, string>();

  await Promise.all(
    selections.map(async ({ dataset, version }) => {
      const graphIri = datasetGraphIri(dataset.id);
      datasetIdByGraphIri.set(graphIri, dataset.id);
      versionIdByDatasetId.set(dataset.id, version.id);
      const graphNode = namedNode(graphIri);

      for (const file of version.files) {
        let text: string;
        if (file.text !== undefined) {
          // User-uploaded datasets carry their content inline.
          text = file.text;
        } else {
          // `file.path` is root-relative to the app's own mount point (e.g.
          // "data/ontologies/rdf.ttl"), not the site root — this app may be
          // mounted at a nested path on a host site (see vite.config.ts's
          // relative `base`), so resolution must go through Vite's injected
          // BASE_URL rather than an absolute "/..." path.
          const url = `${import.meta.env.BASE_URL}${file.path}`;
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(
              `Failed to fetch ${url}: ${response.status} ${response.statusText}`,
            );
          }
          text = await response.text();
        }
        const { quads, prefixes: filePrefixes } = parseRdfFile(text, file);

        for (const [prefix, namespaceIri] of Object.entries(filePrefixes)) {
          prefixes.register(prefix, namespaceIri);
        }
        for (const quad of quads) {
          store.addQuad(quad.subject, quad.predicate, quad.object, graphNode);
        }
      }
    }),
  );

  return { store, prefixes, datasetIdByGraphIri, versionIdByDatasetId };
}
